import { Component, OnInit } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ConfirmModalData } from '@app/core/components/grid/modals/confirm-modal/confirm-modal.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { PrintService } from '@app/core/services/print.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { ConsignmentPlansConsignmentContentGet, ConsignmentPlansService } from '@app/services/consignment-plans.service';
import { LogisticService } from '@app/services/logistic.service';
import { OrderheadService } from '@app/services/orderhead.service';
import { PdfService } from '@app/services/pdf.service';

@Component({
    selector: 'tt-consignment-plans-consignment-content',
    templateUrl: './consignment-plans-consignment-content.component.html',
    styleUrls: ['./consignment-plans-consignment-content.component.css'],
})
export class ConsignmentPlansConsignmentContentComponent implements OnInit {
    params: { consignment_keyno: string; custact_no: string };

    get?: ConsignmentPlansConsignmentContentGet;

    labourers: Item[] = [];

    stocklocationButtons: FormButton[] = [{ id: 'search', type: 'primary', icon: 'fas fa-search', beforeGoto: () => this.beforeGoto(), goto: 'consignmentplansdeliverylocation' }];

    grid: GridOptions = new Grid()
        .setRememberId('w_consignemtplansconsignmentcontent.grid')
        .setLoadDataFunction({ method: 3802, parameters: () => this.params })
        .setKendoConfig({ aggregate: false, pageable: false, height: null });

    printers: Item[] = [];

    printing = false;

    updatingModus = false;

    completing = false;

    constructor(
        private consignmentPlans: ConsignmentPlansService,
        private state: StateService,
        private remember: RememberService,
        private modal: ModalService,
        private logistic: LogisticService,
        private pdf: PdfService,
        private print: PrintService,
        private orderhead: OrderheadService,
    ) {
        this.params = this.state.getStateParams() as { consignment_keyno: string; custact_no: string };
    }

    public onLabourerChanged(event: string) {
        const labourer = this.labourers.find((item) => item.item_id === event);

        if (!!labourer) {
            this.get!.labour_name = labourer.item_name!;
            this.remember.remember('consignmentplansdelivery.labour_no', labourer.item_id);
            this.remember.remember('consignmentplansdelivery.labour_name', labourer.item_name);
        }
    }

    public async changeDriver() {
        await this.beforeGoto();
        this.state.go('consignmentplansdeliverydriver', { argtype: 'order', argvalue: this.get!.order_internal_no });
    }

    public async printPickList() {
        if (!this.get) return;

        this.printing = true;

        try {
            const confirmed = await this.modal.openConfirmDialog({ type: 'secondary', title: 'confirm', translateTitle: true, message: '' });

            if (confirmed) {
                const { errorcode, errormessage } = await this.consignmentPlans.printPickListReport({ order_internal_no: this.get.order_internal_no, p2_printer_keyno: this.get.printer });

                if (errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
                } else {
                    this.modal.show({ type: 'success', title: 'success', translateTitle: true, message: errormessage });
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.printing = false;
        }
    }

    public async updateModus() {
        if (!this.get) return;

        this.updatingModus = true;

        try {
            const { errorcode, errormessage } = await this.consignmentPlans.resetConsignmentPlansContentQuanitites({ consignment_keyno: this.get.consignment_keyno });

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.grid.gridfunc?.read();
            this.updatingModus = false;
        }
    }

    public async complete(withWaybill: boolean) {
        if (!this.get || !this.grid.gridfunc) return;

        this.completing = true;

        try {
            const dates = new Set(this.grid.gridfunc!.getIsSelectedRows().map((row) => (typeof row['cpcc_date'] === 'string' ? row['cpcc_date'] : new Date(row['cpcc_date'].getTime() - row['cpcc_date'].getTimezoneOffset() * 60000).toISOString().split('T')[0])));
            const confirmModalData: ConfirmModalData = { type: 'warning', title: 'confirm', translateTitle: true, message: 'are_you_sure_you_want_to_complete_lines', translateMessage: true };

            if (dates.size > 1) {
                confirmModalData.type = 'danger';
                confirmModalData.title = 'warning';
                confirmModalData.message = 'different_delivery_dates_on_lines_are_you_sure';
            }
            const confirmed = await this.modal.openConfirmDialog(confirmModalData);

            const { locked } = await this.orderhead.getOrderHead({ order_internal_no: this.get.order_internal_no });

            if ('' + locked === '1') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: 'tt_cpcc_complete_locked_error', translateMessage: true });
                return;
            }

            if (!confirmed) return;

            if (this.get.show_print === '1') {
                await this.completeDeliveryWithPrint(withWaybill);
            } else {
                await this.completeDelivery(this.grid.gridfunc.getAllRows().map((row) => ({ consignmentline_keyno: row['consignmentline_keyno'], ordered_quantity: row['cpcc_qty'] })));
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.completing = false;
        }
    }

    private async completeDeliveryWithPrint(withWayBill: boolean) {
        if (!this.get || !this.grid.gridfunc) return;

        await this.completeDelivery(this.grid.gridfunc.getAllRows().map((row) => ({ consignmentline_keyno: row['consignmentline_keyno'], ordered_quantity: row['cpcc_qty'], cancel_rest: row['cancel_rest'] })));

        if (this.get.printer > '0') {
            if (this.get.printer !== '9999999999') {
                await this.printReport(this.get.reportdef_keyno);

                if (withWayBill) {
                    await this.printReport(this.get.reportdef_keyno_waybill);
                }
            }

            this.state.go('consignmentplansdelivery');
        } else {
            this.modal.show({ type: 'danger', title: 'printer_not_selected', translateTitle: true, message: 'printer_not_selected', translateMessage: true });
        }
    }

    private async completeDelivery(rows: any[]) {
        if (!this.get) throw Error('Get is not loaded');

        const { error_code, error_message } = await this.consignmentPlans.consignmentPlansConsignmentContentComplete({ test: '', delivery_note: this.get.delivery_note, records: rows });

        if (error_code !== '0') {
            this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: error_message });
            throw Error('Error during completedelivery >>: ' + error_code + error_message);
        }
    }

    private async printReport(reportDefKeyno: string) {
        const printerParams = await this.getPrinterParams(reportDefKeyno);

        await this.print.print(printerParams);
    }

    private async getPrinterParams(reportDefKeyno: string) {
        if (!this.get) return;

        const reportDefinitions = (await this.pdf.loadP2ReportDef(+reportDefKeyno))[0];

        return {
            reportKeyno: reportDefinitions.printserver_keyno,
            printerKeyno: this.get.printer,
            parameters: { consignment_keyno: this.get.consignment_keyno },
            printerProperties:
                reportDefinitions.paper_size_id === 'custom'
                    ? {
                          scale: 'none',
                          pageAutoSize: false,
                          paperSize: { width: reportDefinitions.width, height: reportDefinitions.height },
                      }
                    : {
                          isLandscape: reportDefinitions.isLandscape,
                          pageAutoSize: true,
                          copies: 1,
                      },
        };
    }

    private async beforeGoto() {
        if (!this.get) return;

        await this.remember.remember(`consignmentplansdeliverycontent.${this.params.consignment_keyno}.delivery_note`, this.get.delivery_note);
        await this.setTemporaryQuantities();
    }

    private async setTemporaryQuantities() {
        const rows: any[] = [];

        for (let row of this.grid.gridfunc?.getRows() ?? []) {
            console.log(row);
            rows.push({ ordered_quantity: row['cpcc_qty'], consignmentline_keyno: row['consignmentline_keyno'], item_is_selected: row['is_selected'], cancel_rest: row['cancel_rest'] });
        }

        console.log(JSON.stringify(rows[0], null, 2));

        const { error_code, error_message } = await this.consignmentPlans.saveTemporaryQuantities({ records: rows });

        if (error_code !== '0') {
            this.modal.show({ title: 'error', translateTitle: true, message: error_message });

            throw Error(error_message);
        }
    }

    private async loadGet() {
        this.get = await this.consignmentPlans.getConsignmentPlansConsignmentContent({ consignment_keyno: this.params.consignment_keyno });
    }

    private async loadPrinters() {
        this.printers = await this.logistic.getPrinters();
        this.printers.push({ item_id: '9999999999', item_name: 'debug no print' });
    }

    private async loadLabourers() {
        this.labourers = await this.consignmentPlans.getConsignmentPlansDeliveryLabourers();
    }

    ngOnInit(): void {
        this.loadGet();
        this.loadPrinters();
        this.loadLabourers();
    }
}
